import React, {Fragment, useEffect, useState} from "react";
import {Dialog, DialogTitle, Grid, IconButton, TextField, Tooltip, Typography} from "@mui/material";
import {object, TypeOf, z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useTranslation} from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleService from "@/services/att/services/ScheduleService";
import {DatePicker} from "@mui/x-date-pickers";
import CompanyService from "@/services/auth/CompanyService";
import {parse} from "date-fns";

export function EditAction({setInfo}: { setInfo: (v: boolean) => void }) {
  const {t} = useTranslation()
  return (
    <Tooltip title={t("att.schedule.Shift schedule")}>
      <IconButton sx={{ "&:hover": { backgroundColor: "transparent" }}} onClick={() => {
        setInfo(true)
      }}>
        <i className="icon iconfont icon-ic_view" style={{ cursor: 'pointer', color: 'rgb(117, 130, 138)' }}></i>
        <span style={{
          fontSize: '14px',
          color: 'rgb(117, 130, 138)',
          cursor: "pointer",
          marginLeft: '4px',
          marginTop: '-2px'
        }}>{t("att.schedule.Shift schedule")}</span>
      </IconButton>
    </Tooltip>
  )
}

export interface ScheduleEditProps {
  scheduleId: string,
}
const ScheduleEdit: React.FC<ScheduleEditProps> = (props) => {
  const [info, setInfo] = useState<boolean>(false)
  const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy")
  useEffect(() => {
    CompanyService.dateFormat({method: 'get'}).then((res) => {
      const date_format = res.data?.date_format?.toString() || "MM/dd/yyyy";
      setDateFormat(date_format);
    });
  }, [])
  return (
    <Fragment>
      <Grid item>
        <EditAction setInfo={setInfo}/>
      </Grid>
      <EditScheduleDialog info={info} setInfo={setInfo} scheduleId={props.scheduleId} dateFormat={dateFormat}/>
    </Fragment>
  )
}

const scheduleSchema = object({
  employee_name: z.string(),
  timesheet_name: z.string(),
})
type scheduleType = TypeOf<typeof scheduleSchema>;

const parseSafeDate = (dateStr: string | undefined | null, dateFormat: string) => {
  if (!dateStr) return null;
  const d = parse(dateStr, dateFormat, new Date(0));
  return d;
}

const EditScheduleDialog = ({info, setInfo, scheduleId, dateFormat}: {
  info: boolean;
  setInfo: (val:boolean)=>void;
  scheduleId:string;
  dateFormat: string
}) => {
  const {t} = useTranslation();
  const [start_date, setStartDate] = React.useState<Date | null>(null);
  const [end_date, setEndDate] = React.useState<Date | null>(null);

  const scheduleForm = useForm<scheduleType>({
    resolver: zodResolver(scheduleSchema),
  });

  useEffect(() => {
    if(!info || !scheduleId) return;
    ScheduleService.retrieve(scheduleId).then((res) => {
      const start = parseSafeDate(res.data.assigned_start, dateFormat);
      const end = parseSafeDate(res.data.assigned_end, dateFormat);
      setStartDate(start);
      setEndDate(end);
      scheduleForm.setValue('employee_name', res.data.employee_name ?? '');
      scheduleForm.setValue('timesheet_name', res.data.timesheet_name ?? '');
    })
  }, [info, scheduleId, scheduleForm]);

  return (
    <Dialog open={info}>
      <DialogTitle style={{marginBottom: "-25px"}}>
        {t("att.schedule.Shift schedule")}
        <IconButton
          aria-label="close"
          onClick={() => setInfo(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon/>
        </IconButton>
      </DialogTitle>
      <Grid style={{marginLeft: "10px", backgroundColor: '#fff', borderRadius: '8px', padding: '0 15px 15px 15px'}}>
        <Grid container>
          <Grid container component={"form"} columnSpacing={4} rowSpacing={2}>
            <Grid item xs={12}/>
            <Grid item xs={6}>
              <Typography>
                {t("common.Select person")}
              </Typography>
              <TextField
                hiddenLabel
                fullWidth
                sx={{padding: '10px 10px 10px 0'}}
                InputProps={{readOnly: true}}
                disabled
                {...scheduleForm.register("employee_name")}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography>
                {t("common.Select timesheet")}
              </Typography>
              <TextField
                hiddenLabel
                fullWidth
                sx={{padding: '10px 10px 10px 0'}}
                InputProps={{readOnly: true}}
                disabled
                {...scheduleForm.register("timesheet_name")}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography>
                {t("common.Start Date")}
              </Typography>
              <DatePicker
                value={start_date}
                onChange={(newValue) => {
                  setStartDate(newValue);
                }}
                disabled
                inputFormat={dateFormat}
                renderInput={(params) => <TextField fullWidth {...params} />}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography>
                {t("common.End Date")}
              </Typography>
              <DatePicker
                value={end_date}
                onChange={(newValue) => {
                  setEndDate(newValue);
                }}
                disabled
                inputFormat={dateFormat}
                renderInput={(params) => <TextField fullWidth {...params} />}
              />
            </Grid>
            <Grid item xs={6}/>
            <Grid item xs={6}/>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  )
}
export default ScheduleEdit;
